import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { CrmCityDto, CrmConversationListItemDto, CrmConversationsQuery } from '@upme/contracts';
import {
  CrmConversationPresenceDto,
  CrmSendConversationConflictDto,
  CrmSendConversationMessageDto,
} from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { cn } from '@upme/ui/lib/utils';
import { messagingChannelSelectSuffix } from '@upme/view-logic';
import { HTTPError } from 'ky';
import { MessageSquare, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../app/api-client';
import { useAuth } from '../../auth/auth-context';
import {
  crmConversationMessagesOptions,
  crmConversationsOptions,
  crmMessagingChannelsOptions,
} from '../queries';
import { CRM_INBOX_CARD } from './crm-panel-surface';

type Props = {
  filters: CrmConversationsQuery;
  cities: ReadonlyArray<CrmCityDto>;
  onFiltersChange: (next: CrmConversationsQuery) => void;
};

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function CrmConversationsInbox({ filters, cities, onFiltersChange }: Props) {
  const { adminRoles } = useAuth();
  const isSuperAdmin = adminRoles.includes('SUPERADMIN');
  const canManageTelegram =
    isSuperAdmin || adminRoles.includes('CITY_MODERATOR') || adminRoles.includes('CRM_MANAGER');
  const queryClient = useQueryClient();
  const { data: channelsMeta } = useSuspenseQuery(crmMessagingChannelsOptions());
  const [draft, setDraft] = useState('');
  const [conflict, setConflict] = useState<{
    colleagueMessage: { body: string | null; sentByDisplayName: string | null };
    draftBody: string;
  } | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: list, isLoading, isError } = useQuery(crmConversationsOptions(filters));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected: CrmConversationListItemDto | undefined = useMemo(
    () => list?.items.find((i) => i.id === selectedId),
    [list?.items, selectedId],
  );

  const messagesQuery = useQuery({
    ...crmConversationMessagesOptions(selectedId ?? ''),
    enabled: Boolean(selectedId),
  });

  const baselineMessageId = messagesQuery.data?.items.at(-1)?.id;

  const presenceQuery = useQuery({
    queryKey: ['admin', 'crm', 'conversations', selectedId, 'presence'],
    queryFn: async () => {
      const data = await api.get(`admin/crm/conversations/${selectedId}/presence`).json();
      return CrmConversationPresenceDto.parse(data);
    },
    enabled: Boolean(selectedId),
    refetchInterval: 2_000,
  });

  const touchPresence = useCallback(
    async (payload: { viewing?: boolean; typing?: boolean }) => {
      if (!selectedId) return;
      const data = await api.post(`admin/crm/conversations/${selectedId}/presence`, { json: payload }).json();
      queryClient.setQueryData(['admin', 'crm', 'conversations', selectedId, 'presence'], data);
    },
    [queryClient, selectedId],
  );

  useEffect(() => {
    if (!selectedId) return;
    void touchPresence({ viewing: true, typing: false });
    const id = setInterval(() => {
      void touchPresence({ viewing: true });
    }, 10_000);
    return () => {
      clearInterval(id);
      void touchPresence({ viewing: false, typing: false });
    };
  }, [selectedId, touchPresence]);

  useEffect(() => {
    if (!selectedId) return;
    void api
      .get(`admin/crm/conversations/${selectedId}/draft`)
      .json<{ body: string | null }>()
      .then((res) => {
        if (res.body) setDraft(res.body);
      })
      .catch(() => undefined);
  }, [selectedId]);

  const removeMessage = useMutation({
    mutationFn: async (messageId: string) => {
      if (!selectedId) return;
      await api.delete(`admin/crm/conversations/${selectedId}/messages/${messageId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'conversations'] });
      if (selectedId) {
        void queryClient.invalidateQueries({
          queryKey: ['admin', 'crm', 'conversations', selectedId, 'messages'],
        });
      }
    },
  });

  const send = useMutation({
    mutationFn: async (opts: { forceSend?: boolean }) => {
      if (!selectedId || !draft.trim()) return;
      const clientSendId = crypto.randomUUID();
      try {
        const data = await api
          .post(`admin/crm/conversations/${selectedId}/messages`, {
            json: {
              body: draft.trim(),
              ...(baselineMessageId ? { baselineMessageId } : {}),
              ...(opts.forceSend ? { forceSend: true } : {}),
              clientSendId,
            },
          })
          .json();
        return CrmSendConversationMessageDto.parse(data);
      } catch (err) {
        if (err instanceof HTTPError && err.response.status === 409) {
          const body = CrmSendConversationConflictDto.parse(await err.response.json());
          throw Object.assign(new Error('conflict'), { conflict: body });
        }
        throw err;
      }
    },
    onSuccess: () => {
      setDraft('');
      setConflict(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'conversations'] });
      if (selectedId)
        void queryClient.invalidateQueries({
          queryKey: ['admin', 'crm', 'conversations', selectedId, 'messages'],
        });
    },
    onError: (err: Error & { conflict?: CrmSendConversationConflictDto }) => {
      if (err.conflict) {
        setConflict({
          colleagueMessage: err.conflict.colleagueMessage,
          draftBody: err.conflict.draftBody,
        });
        setDraft(err.conflict.draftBody);
      }
    },
  });

  const outboundPolicy = messagesQuery.data?.outbound;
  const canSend = outboundPolicy?.allowed === true;
  const sendBlockedReason = outboundPolicy?.allowed === false ? outboundPolicy.reason : null;

  const typingLabel = useMemo(() => {
    const typing = presenceQuery.data?.participants.filter((p) => p.typing) ?? [];
    if (typing.length === 0) return null;
    const names = typing.map((p) => p.displayName).join(', ');
    return `Менеджер ${names} печатает ответ…`;
  }, [presenceQuery.data?.participants]);

  const viewersLabel = useMemo(() => {
    const viewing = presenceQuery.data?.participants.filter((p) => p.viewing && !p.typing) ?? [];
    if (viewing.length === 0) return null;
    return `Смотрят: ${viewing.map((p) => p.displayName).join(', ')}`;
  }, [presenceQuery.data?.participants]);

  return (
    <div className="relative flex min-w-0 flex-1 flex-col gap-4 lg:min-h-[calc(var(--crm-viewport-h)-0.5rem)] lg:flex-row lg:items-stretch">
      <div className={cn(CRM_INBOX_CARD, 'w-full lg:w-[min(22rem,100%)] lg:shrink-0')}>
        <div className="space-y-3 border-b border-border p-4">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-xl font-semibold">Переписки</h1>
            <div className="flex gap-3 text-sm">
              {canManageTelegram ? (
                <Link to="/crm/telegram" className="text-muted-foreground underline-offset-2 hover:underline">
                  Telegram
                </Link>
              ) : null}
              {isSuperAdmin ? (
                <Link
                  to="/crm/instagram"
                  search={{ connected: undefined, error: undefined }}
                  className="text-muted-foreground underline-offset-2 hover:underline"
                >
                  Instagram
                </Link>
              ) : null}
            </div>
          </div>
          <input
            type="search"
            placeholder="Поиск…"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={filters.q ?? ''}
            onChange={(e) => {
              const { q: _q, ...rest } = filters;
              const v = e.target.value.trim();
              onFiltersChange(v ? { ...rest, q: v } : rest);
            }}
          />
          <div className="flex flex-wrap gap-2">
            <select
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              value={filters.cityId ?? ''}
              onChange={(e) => {
                const { cityId: _c, ...rest } = filters;
                onFiltersChange(e.target.value ? { ...rest, cityId: e.target.value } : rest);
              }}
            >
              <option value="">Все города</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              value={filters.channel ?? ''}
              onChange={(e) => {
                const { channel: _ch, ...rest } = filters;
                onFiltersChange(
                  e.target.value
                    ? { ...rest, channel: e.target.value as CrmConversationsQuery['channel'] }
                    : rest,
                );
              }}
            >
              <option value="">Все каналы</option>
              {channelsMeta.channels.map((ch) => (
                <option key={ch.kind} value={ch.kind}>
                  {ch.labelRu}
                  {messagingChannelSelectSuffix(ch)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <li className="p-4 text-sm text-muted-foreground">Загрузка…</li>
          ) : isError ? (
            <li className="p-4 text-sm text-destructive">Не удалось загрузить переписки</li>
          ) : list?.items.length === 0 ? (
            <li className="flex flex-col items-center gap-2 p-8 text-center text-muted-foreground">
              <MessageSquare className="size-10 opacity-40" />
              <p className="text-sm">Пока нет диалогов</p>
              <p className="text-xs">
                {canManageTelegram ? (
                  <>
                    Подключите корпоративный Telegram в{' '}
                    <Link to="/crm/telegram" className="underline">
                      настройках
                    </Link>
                    , затем воркер подтянет диалоги.
                  </>
                ) : (
                  'После подключения Telegram администратором диалоги появятся здесь.'
                )}
              </p>
            </li>
          ) : (
            list?.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={cn(
                    'w-full border-b border-border px-4 py-3 text-left hover:bg-muted/50',
                    selectedId === item.id && 'bg-muted',
                  )}
                  onClick={() => setSelectedId(item.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate font-medium">{item.contactLabel}</p>
                    {item.unreadForManager > 0 ? (
                      <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                        {item.unreadForManager}
                      </span>
                    ) : null}
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.channelAccountLabel} · {formatWhen(item.lastMessageAt)}
                  </p>
                  {item.previewText ? (
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.previewText}</p>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>

      <div className={cn(CRM_INBOX_CARD, 'min-h-[20rem] flex-1 p-4')}>
        {!selected ? (
          <p className="text-sm text-muted-foreground">Выберите переписку слева</p>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div>
              <h2 className="text-lg font-semibold">{selected.contactLabel}</h2>
              <p className="text-sm text-muted-foreground">{selected.channelAccountLabel}</p>
              {viewersLabel ? <p className="mt-1 text-xs text-muted-foreground">{viewersLabel}</p> : null}
              {typingLabel ? <p className="text-xs text-primary">{typingLabel}</p> : null}
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-md border border-border bg-muted/20 p-3">
              {messagesQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Загрузка сообщений…</p>
              ) : messagesQuery.data?.items.length === 0 ? (
                <p className="text-sm text-muted-foreground">Сообщений пока нет</p>
              ) : (
                messagesQuery.data?.items.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      'group relative max-w-[85%] rounded-lg px-3 py-2 text-sm',
                      msg.direction === 'OUTBOUND' ? 'ml-auto bg-primary/15' : 'bg-background border',
                    )}
                  >
                    {isSuperAdmin ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute -right-1 -top-1 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                        aria-label="Удалить сообщение"
                        disabled={removeMessage.isPending}
                        onClick={() => {
                          if (window.confirm('Удалить сообщение из переписки?')) {
                            removeMessage.mutate(msg.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                    {msg.direction === 'OUTBOUND' && msg.sentByDisplayName ? (
                      <p className="mb-1 text-xs font-medium text-muted-foreground">
                        {msg.sentByDisplayName}
                      </p>
                    ) : null}
                    <p className="whitespace-pre-wrap">{msg.body ?? '—'}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{formatWhen(msg.occurredAt)}</p>
                  </div>
                ))
              )}
            </div>
            {conflict ? (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                <p className="font-medium">Коллега уже ответил. Проверьте своё сообщение.</p>
                <p className="mt-2 whitespace-pre-wrap rounded bg-background/80 p-2">
                  {conflict.colleagueMessage.body ?? '—'}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={() => setConflict(null)}>
                    Изменить ответ
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      send.mutate({ forceSend: true });
                    }}
                    disabled={send.isPending}
                  >
                    Всё равно отправить
                  </Button>
                </div>
              </div>
            ) : null}
            {sendBlockedReason ? <p className="text-sm text-muted-foreground">{sendBlockedReason}</p> : null}
            {canSend ? (
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  send.mutate({});
                }}
              >
                <input
                  className="min-w-0 flex-1 rounded-md border border-input px-3 py-2 text-sm"
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    if (typingTimer.current) clearTimeout(typingTimer.current);
                    void touchPresence({ viewing: true, typing: true });
                    typingTimer.current = setTimeout(() => {
                      void touchPresence({ viewing: true, typing: false });
                    }, 2_000);
                  }}
                  placeholder="Ответ клиенту…"
                />
                <Button type="submit" disabled={!draft.trim() || send.isPending}>
                  {send.isPending ? '…' : 'Отправить'}
                </Button>
              </form>
            ) : (
              <p className="text-xs text-muted-foreground">
                {canManageTelegram ? (
                  <>
                    Ответ доступен для Telegram после{' '}
                    <Link to="/crm/telegram" className="underline">
                      подключения аккаунта
                    </Link>
                    .
                  </>
                ) : (
                  'Ответ в Telegram доступен после подключения корпоративного аккаунта администратором.'
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
